import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import isPropValid from '@emotion/is-prop-valid';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useKeyboardEvent, useWindowSize } from '@react-hookz/web';
import {
  motion,
  motionValue,
  useMotionTemplate,
  useScroll,
  useTransform,
} from 'framer-motion';
import { disablePageScroll, enablePageScroll } from 'scroll-lock';

import { screen } from '@/components/common/breakpointsV2';
import { ActionButtonV2, ButtonLinkV2 } from '@/components/common/Button';
import { zIndex } from '@/components/common/z-index';
import { Banner } from '@/components/layout/Banner';
import { NavLink } from '@/components/layout/common';
import { MultiverseLogo } from '@/components/layout/MultiverseLogo';
import { heroTransition } from '@/lib/animations';
import { useMediaQuery } from '@/lib/useMediaQuery';
import CloseIcon from '@/public/images/close-v2.svg';
import HamburgerIcon from '@/public/images/hamburger-v2.svg';

import { useNavContext } from './context/NavContext';
import { SubNavControllerContextProvider } from './context/SubNavControllerContext';
import { TopLevelNavItem } from './TopLevelNavItem';

import type { BannerType } from '@/components/layout/Banner';
import type { Theme } from '@/components/layout/common';
import type { SanityNavigationType } from '@/types/sanity';
import type { MutableRefObject } from 'react';

export interface NavigationBarV2Props {
  theme: Theme;
  navigation: SanityNavigationType;
  isBannerOpen?: boolean;
  banner?: BannerType;
  onCloseBanner?: () => void;
}

const TRANSITION_LENGTH = 50;

export const NavigationBarV2 = ({
  theme,
  navigation,
  isBannerOpen,
  banner,
  onCloseBanner,
}: NavigationBarV2Props) => {
  const { scrollY } = useScroll();
  const { height: windowHeight } = useWindowSize();

  const [navControls] = useNavContext();
  const { scrollThreshold, themeOverride } = navControls;

  const mobileNavRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLUListElement>(null);

  const [hasChangedAppearance, setChangedAppearance] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [activeNavItemId, setActiveNavItemId] = useState<string | null>(null);

  const isTopLevelItem = useMemo(
    () =>
      navigation.navItems.find((navItem) => navItem._key === activeNavItemId)
        ?.topLevelLinkOnly || false,
    [activeNavItemId, navigation.navItems],
  );

  const isNavActive = Boolean(activeNavItemId) || isMobileNavOpen;
  const isSubNavOpen = Boolean(activeNavItemId) && !isTopLevelItem;

  const isDesktop = useMediaQuery(screen.mdQuery);
  const isMobile = !isDesktop;

  const derivedTheme = themeOverride ?? theme;
  const shouldUseLightElements =
    derivedTheme === 'light' && !isNavActive && !hasChangedAppearance;

  const opacityTransform = useTransform(
    scrollY,
    [scrollThreshold, scrollThreshold + TRANSITION_LENGTH],
    [0, 1],
  );

  useEffect(() => {
    return scrollY.on('change', (value) =>
      setChangedAppearance(value >= scrollThreshold + TRANSITION_LENGTH / 2),
    );
  }, [scrollThreshold, scrollY]);

  useKeyboardEvent('Escape', () => {
    setIsMobileNavOpen(false);
    setActiveNavItemId(null);
  });

  const handleOnTopLevelNavItemClick = (id) => {
    setActiveNavItemId(id === activeNavItemId ? null : id);
  };

  const handleOnTopLevelNavLinkHover = (event, id) => {
    if (isDesktop) setActiveNavItemId(id);
  };

  const handleOnNavMouseLeave = () => {
    if (isDesktop) {
      setActiveNavItemId(null);
    }
  };

  const toggleMobileMenu = useCallback(() => {
    setIsMobileNavOpen((isOpen) => {
      if (!isOpen && isMobile) {
        disablePageScroll(scrollRef.current);

        return true;
      }
      setActiveNavItemId(null);
      enablePageScroll(scrollRef.current);

      return false;
    });
  }, [isMobile]);

  useEffect(() => {
    // catch screen size changing
    if (isDesktop) {
      enablePageScroll();
    }

    return () => {
      enablePageScroll();
    };
  }, [isDesktop]);

  const CTA = navigation.primaryCTA && (
    <ButtonLinkV2
      href={navigation.primaryCTA.buttonUrl}
      variant="solid"
      backgroundOfParent={shouldUseLightElements ? 'deep-space' : 'white'}
      size="md"
      ariaLabel={navigation.primaryCTA.buttonAccessibleLabel}
    >
      {navigation.primaryCTA.buttonText}
    </ButtonLinkV2>
  );

  return (
    <>
      {isBannerOpen && <Banner banner={banner} onClose={onCloseBanner} />}
      <GlobalNav
        theme={derivedTheme}
        isNavActive={isNavActive || hasChangedAppearance}
        isSubNavOpen={isSubNavOpen}
        transition={{ ...heroTransition, duration: 0.4 }}
        style={{
          backgroundColor: useMotionTemplate`rgba(255, 255, 255, ${
            isNavActive ? motionValue(1) : opacityTransform
          })`,
        }}
      >
        <SafariHack
          initial={{ y: '-100%' }}
          animate={{
            y: '0%',
          }}
          transition={heroTransition}
        >
          <GlobalNavContent>
            <LogoContainer>
              <LogoLink href="/" ariaLabel="Multiverse home">
                <MultiverseLogo
                  isV2
                  theme={shouldUseLightElements ? 'light' : 'dark'}
                />
              </LogoLink>

              <ActionButtonV2
                leftIcon={isMobileNavOpen ? <CloseIcon /> : <HamburgerIcon />}
                variant="flat"
                background={shouldUseLightElements ? 'dark' : 'light'}
                size="sm"
                onClick={toggleMobileMenu}
                aria-expanded={isMobileNavOpen}
                aria-controls="subnavigation-list"
                aria-haspopup="true"
                aria-label={
                  isMobileNavOpen ? 'Close navigation' : 'Open navigation'
                }
              >
                {isMobileNavOpen ? 'Close' : 'Menu'}
              </ActionButtonV2>
            </LogoContainer>

            {isDesktop && (
              <>
                <DesktopNav aria-label="main menu" id="subnavigation-list">
                  <DesktopNavList onMouseLeave={() => handleOnNavMouseLeave()}>
                    {navigation.navItems.map(
                      ({ title, url, _key, topLevelLinkOnly, ...rest }) => (
                        <TopLevelNavItem
                          key={_key}
                          isThisSubNavOpen={activeNavItemId === _key}
                          onClickNavLink={handleOnTopLevelNavItemClick}
                          onMouseEnterNavLink={handleOnTopLevelNavLinkHover}
                          linkHref={url}
                          itemId={_key}
                          isDesktop={isDesktop}
                          topLevelLinkOnly={topLevelLinkOnly}
                          title={title}
                          columns={rest}
                          isAnySubNavOpen={isSubNavOpen}
                          theme={derivedTheme}
                        />
                      ),
                    )}
                  </DesktopNavList>
                </DesktopNav>
                {CTA && <DesktopCtaSection>{CTA}</DesktopCtaSection>}
              </>
            )}

            {isMobile && (
              <MobileNav
                initial="closed"
                animate={isMobileNavOpen ? 'open' : 'closed'}
                variants={{
                  closed: {
                    opacity: 0,
                    transitionEnd: {
                      visibility: 'hidden',
                    },
                  },
                  open: { opacity: 1, visibility: 'visible' },
                }}
                ref={mobileNavRef}
                style={{
                  height:
                    windowHeight -
                    (mobileNavRef?.current?.getBoundingClientRect().top ?? 0),
                }}
                transition={{ ...heroTransition, duration: 0.4 }}
              >
                <MobileNavList
                  ref={scrollRef}
                  transition={heroTransition}
                  variants={{
                    closed: { y: '-50%', opacity: 0 },
                    open: { y: '0%', opacity: 1 },
                  }}
                >
                  <SubNavControllerContextProvider>
                    {navigation.navItems.map(
                      ({ title, url, _key, topLevelLinkOnly, ...rest }) => (
                        <TopLevelNavItem
                          key={_key}
                          isThisSubNavOpen={activeNavItemId === _key}
                          onClickNavLink={handleOnTopLevelNavItemClick}
                          onMouseEnterNavLink={handleOnTopLevelNavLinkHover}
                          linkHref={url}
                          itemId={_key}
                          isDesktop={isDesktop}
                          topLevelLinkOnly={topLevelLinkOnly}
                          title={title}
                          columns={rest}
                          isAnySubNavOpen={isSubNavOpen}
                          theme={derivedTheme}
                        />
                      ),
                    )}
                  </SubNavControllerContextProvider>
                </MobileNavList>
                {CTA && (
                  <MobileCtaSection
                    variants={{
                      closed: { y: '100%' },
                      open: { y: '0%' },
                    }}
                    transition={heroTransition}
                  >{CTA}</MobileCtaSection>
                )}
              </MobileNav>
            )}
          </GlobalNavContent>
        </SafariHack>
      </GlobalNav>
    </>
  );
};

interface GlobalNavProps {
  theme?: Theme;
  isNavActive?: boolean | MutableRefObject<any>;
  isSubNavOpen?: boolean;
}

const GlobalNav = styled(motion.header, {
  shouldForwardProp: isPropValid,
})<GlobalNavProps>`
  position: sticky;
  top: 0;

  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 320px;
  will-change: background-color, color, border;
  z-index: ${zIndex.navBar};
  transition:
    color 0.6s ease,
    border-bottom 0.6s ease;

  ${({ isNavActive, theme }) => css`
    color: ${isNavActive || theme === 'dark'
      ? 'var(--colour-deep-space)'
      : 'var(--colour-white)'};
    border-bottom: 1px solid
      ${isNavActive ? 'var(--colour-border-subtle)' : 'transparent'};
  `}

  ${screen.md} {
    justify-content: center;

    &::after {
      visibility: ${({ isSubNavOpen }) =>
        isSubNavOpen ? 'visible' : 'hidden'};
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      top: 100%;
      height: calc(100vh - 100%);
      z-index: -1;
      background-color: var(--colour-deep-space);
      opacity: ${({ isSubNavOpen }) => (isSubNavOpen ? '0.24' : '0')};
      transition:
        opacity 0.2s ease,
        visibility 0.2s linear;
      pointer-events: none;
    }
  }
`;

const SafariHack = styled(motion.div)`
  // If both transform and position:sticky are present on the same element, then you can overscroll past the bottom of the page for some reason
  // I'm now using this container to apply the "enter" effect
  ${screen.md} {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`;

const GlobalNavContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  z-index: 2;

  will-change: height;

  ${screen.md} {
    height: var(--spacing-1000);
    align-self: center;

    flex-direction: row;
    max-width: var(--desktop-max-width);
    padding: 0 var(--layout-gutter-v2);
  }
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-200) var(--spacing-250);
  height: var(--spacing-900);

  ${screen.md} {
    border: none;
    padding: var(--spacing-300) 0;
    height: auto;

    button {
      display: none;
    }
  }
`;

const LogoLink = styled(NavLink)`
  height: 22px;
  width: auto;

  ${screen.md} {
    height: 26px;
  }

  svg {
    height: 100%;
    width: auto;
    margin-top: -3px;
    transition: 0.3s ease-out;
  }

  @media (forced-colors: active) and (prefers-color-scheme: dark) {
    svg path {
      fill: white !important;
    }
  }
`;

const MobileNav = styled(motion.nav)`
  position: absolute;
  top: 4.5rem;
  width: 100%;
  height: calc(100vh - 4.5rem);

  background-color: var(--colour-white);

  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: hidden;
`;

const DesktopNav = styled.nav`
  display: flex;
  flex-direction: column;
  flex-direction: row;
  flex-grow: 1;

  &:hover {
    &:after {
      opacity: 1;
    }
  }
`;

const MobileNavList = styled(motion.ul)`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow-y: scroll;
  overflow-x: hidden;
`;

const DesktopNavList = styled.ul`
  display: flex;
  flex-grow: 1;
  flex-direction: row;
  padding-left: var(--spacing-400);
`;

const MobileCtaSection = styled(motion.div)`
  width: 100%;
  background-color: var(--colour-white);

  padding: var(--spacing-200);
  border-top: 1px solid var(--colour-border-subtle);
  display: flex;
  align-items: center;

  /* position: fixed;
  bottom: 0; */
  z-index: 20;

  ${ButtonLinkV2} {
    width: 100%;
    justify-content: center;
  }
`;

const DesktopCtaSection = styled.div`
  justify-self: flex-end;
  display: flex;
  align-items: center;
  position: relative;

  ${ButtonLinkV2} {
    width: 100%;
    justify-content: center;
    font-size: 1rem;
    transition-duration: 0.6s;
  }
`;
